import { Action, State, StateContext, Selector } from '@ngxs/store';
import { NewChannelAction } from './new-channels.actions';
import { PlaylistService } from '../../services/playlist.service';
import { Playlist, PaginationInfo } from '../../models/models';
import { tap, map, switchMap } from 'rxjs/operators';
import { from, forkJoin, of } from 'rxjs';
import { PlaylistPrepare } from '../../utils/prepare-playlist';
import { ImageUploadService } from '../../services/image-upload.service';
import { MediaTypes } from '../../models/consts';
import { Injectable } from '@angular/core';

type Context = StateContext<NewChannelStateModel>;

export interface NewChannelStateModel {
  playlists: Playlist[];
  isPlaylistsLoaded: boolean;
  hasNextPage: boolean;
  nextCursor: string;
  lastUpdated: Playlist | null;
}

@State<NewChannelStateModel>({
  name: 'newChannel',
  defaults: {
    playlists: null,
    isPlaylistsLoaded: false,
    hasNextPage: false,
    nextCursor: null,
    lastUpdated: null
  }
})
@Injectable()
export class NewChannelState {

  constructor(
      private playlistService: PlaylistService,
      private imageUploadService: ImageUploadService
    ) {}

  @Action(NewChannelAction.GetNewChannels)
  fetch({patchState}: StateContext<NewChannelStateModel>) {
    return this.playlistService.getSystemPlaylistsConnection(null, 20, MediaTypes.CHANNEL)
      .pipe(
        tap((paginationInfo: PaginationInfo) => {
          patchState({
            hasNextPage: paginationInfo.pageInfo.hasNextPage,
            nextCursor: paginationInfo.pageInfo.endCursor
          });
        }),
        map(({edges}) => edges.map(({node}) => node)),
        tap((playlists: Playlist[]) => {

          // add artwork to playlist or create collage
          playlists = playlists.map(item => {
            item.artwork = PlaylistPrepare.videoPlaylistArtwork(item);
            return item;
          });
          patchState({
            playlists,
            isPlaylistsLoaded: true
          });
        })
      );
  }

  @Action(NewChannelAction.GetMoreChannels)
  getMore({getState, patchState}: StateContext<NewChannelStateModel>, { count }: NewChannelAction.GetMoreChannels) {
    const state = getState();
    return this.playlistService.getSystemPlaylistsConnection(state.nextCursor, count || 10, MediaTypes.CHANNEL)
      .pipe(
        tap((paginationInfo: PaginationInfo) => {
          patchState({
            hasNextPage: paginationInfo.pageInfo.hasNextPage,
            nextCursor: paginationInfo.pageInfo.endCursor
          });
        }),
        map(({edges}) => edges.map(({node}) => node)),
        tap((playlists: Playlist[]) => {
          // add artwork to playlist or create collage
          playlists = playlists.map(item => {
            item.artwork = PlaylistPrepare.videoPlaylistArtwork(item);
            return item;
          });
          
          patchState({
            playlists: [...state.playlists, ...playlists]
          });
        })
      );
  }

  @Action(NewChannelAction.CreateChannel)
  create({getState, patchState}: StateContext<NewChannelStateModel>, { playlistInput, cover }: NewChannelAction.CreateChannel) {
    const state = getState();
    let newPlaylist: Playlist;
    return this.playlistService.createNewPlaylist({...playlistInput, mediaType: MediaTypes.CHANNEL})
    .pipe(
      tap((playlist) => newPlaylist = playlist),
      switchMap((playlist) => {
        if(!!cover) {
          return from(this.imageUploadService.uploadPlaylistCover(cover, playlist.id))
            .pipe(
              switchMap(() => this.playlistService.updatePlaylistCover(playlist.id)),
              map((logoImageUrl: string) => {
                newPlaylist.logoImageUrl = logoImageUrl;
                return newPlaylist;
              })
            )
        }
        return of(playlist);
      }),
      tap((playlist: Playlist) => {
        playlist.artwork = PlaylistPrepare.videoPlaylistArtwork(playlist);
        const playlists = PlaylistPrepare.sortPlaylists([...state.playlists, playlist]);
        patchState({
          playlists,
          lastUpdated: playlist
        });
      })
    );
  }

  @Action(NewChannelAction.EditChannel)
  edit({getState, patchState}: StateContext<NewChannelStateModel>, { id, playlistInput, cover }: NewChannelAction.EditChannel) {
    const state = getState();
    let newPlaylist: Playlist;
    return this.playlistService.updateNewPlaylist(id, playlistInput)
    .pipe(
      tap((playlist) => newPlaylist = playlist),
      switchMap((playlist) => {
        if(!!cover) {
        return from(this.imageUploadService.uploadPlaylistCover(cover, id))
          .pipe(
            switchMap(() => this.playlistService.updatePlaylistCover(id)),
            map((logoImageUrl: string) => {
              newPlaylist.logoImageUrl = logoImageUrl;
              return newPlaylist;
            })
          );
        }
        return of(playlist);
      }),
      tap((playlist: Playlist) => {
        playlist.artwork = PlaylistPrepare.videoPlaylistArtwork(playlist);
        const playlists = PlaylistPrepare.sortPlaylists([...state.playlists.filter(x => x.id !== playlist.id), playlist]);
        patchState({
          playlists,
          lastUpdated: playlist
        });
      })
    );
  }

  @Action(NewChannelAction.DeleteChannel)
  delete({getState, patchState}: StateContext<NewChannelStateModel>, { id }: NewChannelAction.DeleteChannel) {
    const state = getState();
    return this.playlistService.deleteLibraryPlaylist(id)
    .pipe(
      tap(() => {
        patchState({
          playlists: state.playlists.filter((item) => item.id !== id),
          lastUpdated: null
        });
      })
    );
  }

  @Action(NewChannelAction.UpdateTracksInChannel)
  addTracksToPlaylist({getState, patchState}: StateContext<NewChannelStateModel>, {playlistId}: NewChannelAction.UpdateTracksInChannel) {
  return this.playlistService.updateTracksForPlaylist(playlistId)
    .pipe(
      tap((playlist: Playlist) => {
        const state = getState();
        const updPlaylist = {
          ...state.playlists.find(x => x.id === playlist.id),
          trackCount: playlist.trackCount,
          duration: playlist.duration,
          generalGenre: playlist.generalGenre,
          artwork: PlaylistPrepare.videoPlaylistArtwork(playlist),
          updatedAt: playlist.updatedAt
        };
        patchState({
          playlists: PlaylistPrepare.sortPlaylists([...state.playlists.filter(x => x.id !== playlist.id), updPlaylist]),
          lastUpdated: updPlaylist
        });
      })
    )
  }

  @Action(NewChannelAction.RemoveVideoFromChannel)
  removeTracksFromPlaylist({getState, patchState}: StateContext<NewChannelStateModel>, {trackIds, playlistId}: NewChannelAction.RemoveVideoFromChannel) {
    return forkJoin(...trackIds.map(item =>
      this.playlistService.removeTrack(item, playlistId)
    ))
    .pipe(
      switchMap(() => from(this.playlistService.getPlaylist(playlistId))),
      tap((playlist) => {
        const state = getState();
        if (state.isPlaylistsLoaded) {
          const updPlaylist = {
            ...state.playlists.find(x => x.id === playlist.id),
            trackCount: playlist.trackCount,
            duration: playlist.duration,
            generalGenre: playlist.generalGenre,
            artwork: PlaylistPrepare.videoPlaylistArtwork(playlist),
            updatedAt: playlist.updatedAt
          };
          patchState({
            playlists: PlaylistPrepare.sortPlaylists([...state.playlists.filter(x => x.id !== playlist.id), updPlaylist]),
            lastUpdated: updPlaylist
          });
        }
      })
    )
  }

  @Selector()
  static channel(state: NewChannelStateModel): Playlist[] {
    return state.playlists;
  }

  @Selector()
  static publishedChannel(state: NewChannelStateModel): Playlist[] {
    return state.playlists.filter(x => x.isPublished);
  }

  @Selector()
  static isPlaylistsLoaded(state: NewChannelStateModel): boolean {
    return state.isPlaylistsLoaded;
  }

  @Selector()
  static hasNext(state: NewChannelStateModel): boolean {
    return state.hasNextPage;
  }

  @Selector()
  static lastUpdated(state: NewChannelStateModel): Playlist {
    return state.lastUpdated;
  }
}